import React, { useState, useRef, useEffect } from "react";
import { useEditor, EditorContent, BubbleMenu } from "@tiptap/react";
import {
  Text,
  Description,
  Button,
  ButtonGroup,
  useTheme,
  Modal,
  Input,
  useModal,
} from "@geist-ui/core";
import Link2 from "@geist-ui/icons/link2";

import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import MenuBar from "./MenuBar";
import { getBubbleMenuConfig } from "../../utils/editor";
import { DEFUALT_MODAL_INPUT_STATE } from "../../constants/editor";

import "./styles.css";

const Editor = ({ journal, closeCallback }) => {
  const { content, date, title } = journal;
  const { visible, setVisible, bindings } = useModal();
  const [nodeModalValue, setNodeModalValue] = useState(
    DEFUALT_MODAL_INPUT_STATE
  );
  const modalContentRef = useRef(null);
  const theme = useTheme();

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image,
      Link,
      TextAlign.configure({
        types: ["heading", "paragraph", "list_item", "blockquote"],
      }),
    ],
    content: content,
  });

  useEffect(() => {
    if (modalContentRef.current) {
      setVisible(true);
      console.log("Showing modal", bindings);
    } else {
      setVisible(false);
      console.log("Hiding modal", bindings);
    }
  }, [modalContentRef.current]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddNodeModalInput = (e, nodeType, fieldType) => {
    switch (nodeType) {
      case "image":
        setNodeModalValue((prevState) => {
          const updatedKey = fieldType === "url" ? "url" : "alt";

          return {
            ...prevState,
            image: {
              ...prevState.image,
              [updatedKey]: e.target.value,
            },
          };
        });
        break;
      case "link":
        setNodeModalValue((prevState) => {
          const updatedKey = fieldType === "url" ? "url" : "text";

          return {
            ...prevState,
            link: {
              ...prevState.link,
              [updatedKey]: e.target.value,
            },
          };
        });
        break;
      default:
        break;
    }
  };

  const _renderAddImageNode = () => {
    return (
      <>
        <Modal.Title>Add Image</Modal.Title>
        <Modal.Content>
          <Input
            placeholder="Image URl"
            value={nodeModalValue.image.url}
            icon={<Link2 />}
            onChange={(e) => {
              handleAddNodeModalInput(e, "image", "url");
            }}
          >
            Add Image Url
          </Input>
          <Input
            placeholder="Image Alt Text"
            value={nodeModalValue.image.alt}
            icon={<Link2 />}
            onChange={(e) => {
              handleAddNodeModalInput(e, "image", "alt");
            }}
          >
            Add Image Alt Text
          </Input>
        </Modal.Content>
      </>
    );
  };

  const _renderAddLinkNode = () => {
    return (
      <>
        <Modal.Title>Add Link</Modal.Title>
        <Modal.Content>
          <Input
            placeholder="Link URl"
            value={nodeModalValue}
            icon={<Link2 />}
            onChange={(e) => {
              handleAddNodeModalInput(e, "link", "url");
            }}
          >
            Add Link Url
          </Input>
          <Input
            placeholder="Link Text"
            value={nodeModalValue}
            icon={<Link2 />}
            onChange={(e) => {
              handleAddNodeModalInput(e, "link", "text");
            }}
          >
            Add Link Text
          </Input>
        </Modal.Content>
      </>
    );
  };

  const _renderAddNodeModal = (nodeType) => {
    switch (nodeType) {
      case "image":
        return _renderAddImageNode();
      case "link":
        return _renderAddLinkNode();
      default:
        return null;
    }
  };

  const handleModalClose = () => {
    // Reset modal content and value
    modalContentRef.current = null;
    setNodeModalValue(DEFUALT_MODAL_INPUT_STATE);

    console.log(bindings, modalContentRef.current);
  };

  const handleNodeAdd = (nodeType) => {
    switch (nodeType) {
      case "image":
        editor.chain().focus().setImage(nodeModalValue.image).run();
        handleModalClose();
        break;
      case "link":
        editor.chain().focus().setLink(nodeModalValue.link).run();
        handleModalClose();
        break;
      default:
        break;
    }

    handleModalClose();
  };

  const _renderModalContent = (nodeType) => {
    return (
      <Modal {...bindings} onClose={handleModalClose}>
        {modalContentRef.current}
        <Modal.Action passive onClick={handleModalClose}>
          Cancel
        </Modal.Action>

        <Modal.Action onClick={() => handleNodeAdd(nodeType)}>Add</Modal.Action>
      </Modal>
    );
  };

  // @TODO: Refactor this into a separate component, reused in MenuBar
  const _renderBubbleMenuButton = (icon, action, actionName, buttonTooltip) => {
    let btnClassName = "";
    let isActive = false;
    let additionAction = null;

    if (actionName.startsWith("heading")) {
      const headingLevel = actionName.replace("heading", "");
      isActive = editor.isActive("heading", { level: headingLevel });
    } else {
      isActive = editor.isActive(actionName);
    }

    if (actionName.startsWith("textAlign")) {
      const textAlignDirection = actionName
        .replace("textAlign", "")
        .toLowerCase();
      isActive = editor.isActive({ textAlign: textAlignDirection });
    }

    if (isActive) {
      btnClassName += "active";
    } else {
      btnClassName += "inactive";
    }

    switch (actionName) {
      case "image":
        additionAction = () => {
          modalContentRef.current = _renderAddNodeModal(actionName);
        };
        break;
      case "link":
        additionAction = () => {
          modalContentRef.current = _renderAddNodeModal(actionName);
        };
        break;
      default:
        break;
    }

    return (
      <Button
        className={`editor-btn ${btnClassName}`}
        auto
        scale={2 / 3}
        onClick={() => {
          if (additionAction) {
            console.log(
              "before addition action",
              modalContentRef.current,
              bindings
            );
            additionAction();
            console.log(
              "after addition action",
              modalContentRef.current,
              bindings
            );
          } else {
            action();
          }
        }}
        style={{ color: theme.palette.foreground }}
      >
        {icon}
        {_renderModalContent(actionName)}
      </Button>
    );
  };

  const _renderBubbleMenu = () => {
    const bubbleMenuConfig = getBubbleMenuConfig(editor);

    return editor ? (
      <BubbleMenu editor={editor}>
        <ButtonGroup
          className="editor-btn-group"
          ghost
          type="secondary-light"
          scale={2 / 3}
          style={{ background: theme.palette.background }}
        >
          {Object.entries(bubbleMenuConfig).map(([actionName, config]) => {
            const { icon, action, tooltip } = config;

            return _renderBubbleMenuButton(icon, action, actionName, tooltip);
          })}
        </ButtonGroup>
      </BubbleMenu>
    ) : null;
  };

  return (
    <div className="edit-container">
      <div className="heading">
        <Text h2>{title}</Text>
        <Description title="" content={date} />
      </div>
      {_renderBubbleMenu()}
      <MenuBar
        editor={editor}
        closeCallback={closeCallback}
        journal={journal}
      />
      <EditorContent editor={editor} />
    </div>
  );
};

export default Editor;
